// src/pages/InventoryImportHubPage.js
// Splash page for /admin/import. Tile grid linking to each importer.
// Customer-status spreadsheets (GFL, Prairie, WCI, SB Shop) open the
// reusable CustomerReportImportModal; the all-inventory and Labrie
// importers navigate to their existing dedicated pages.

import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Upload, Database, FileSpreadsheet, Truck, ClipboardList, Factory,
} from "lucide-react";
import CustomerReportImportModal from "../components/CustomerReportImportModal";

const TILES = [
  {
    id:    "all-inventory",
    label: "All Inventory Spreadsheet",
    desc:  "Full column-mapped import for the master inventory sheet. Handles dedupe by VIN / RO/WO / Stock PO.",
    icon:  Database,
    color: "#2563eb",
    type:  "link",
    to:    "/admin/import/inventory",
  },
  {
    id:    "labrie",
    label: "Labrie Factory Update",
    desc:  "Builder-side update from Labrie. Diff preview and selective apply.",
    icon:  Factory,
    color: "#7c3aed",
    type:  "link",
    to:    "/admin/import/labrie",
  },
  {
    id:    "gfl",
    label: "GFL / Superior Update",
    desc:  "Weekly GFL status — Dealer to Contact, GFLUnit #, Superior Notes, factory dates, Scale/No Scale.",
    icon:  Truck,
    color: "#16a34a",
    type:  "modal",
    kind:  "gfl",
  },
  {
    id:    "prairie",
    label: "Prairie Disposal Update",
    desc:  "Customer status — RO/WO, Stock #, VIN, factory dates, Unit Ready at SNA.",
    icon:  Truck,
    color: "#0891b2",
    type:  "modal",
    kind:  "prairie",
  },
  {
    id:    "wci",
    label: "WCI Update",
    desc:  "Customer status with ETA Customer (last column differs from Prairie).",
    icon:  Truck,
    color: "#d97706",
    type:  "modal",
    kind:  "wci",
  },
  {
    id:    "sb",
    label: "SB Shop Updates",
    desc:  "Free-form shop notes — appended to each unit's PDI / Shop Notes thread.",
    icon:  ClipboardList,
    color: "#64748b",
    type:  "modal",
    kind:  "sb",
  },
];

export default function InventoryImportHubPage() {
  const [active, setActive] = useState(null); // tile config when modal is open

  return (
    <div className="container">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Upload size={22} />
        <h2 style={{ margin: 0, fontWeight: 900 }}>Imports</h2>
      </div>
      <div className="muted" style={{ marginBottom: 18 }}>
        Pick the type of update you're importing. Each tile uses the column layout for that specific
        spreadsheet — running the wrong tile against the wrong file will simply find no matches.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 14,
        }}
      >
        {TILES.map((t) => {
          const Icon = t.icon;
          const inner = (
            <div
              className="card"
              style={{
                padding: 16,
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                cursor: "pointer",
                borderTop: `3px solid ${t.color}`,
                height: "100%",
                transition: "transform 0.1s ease, border-color 0.1s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
            >
              <div
                style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: `${t.color}1f`, color: t.color,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  flex: "0 0 auto",
                }}
              >
                <Icon size={20} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: "var(--fg)" }}>{t.label}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>{t.desc}</div>
              </div>
            </div>
          );

          if (t.type === "link") {
            return (
              <Link key={t.id} to={t.to} style={{ textDecoration: "none", color: "inherit" }}>
                {inner}
              </Link>
            );
          }
          return (
            <div key={t.id} onClick={() => setActive(t)}>
              {inner}
            </div>
          );
        })}
      </div>

      {/* General-purpose import tip */}
      <div
        className="card"
        style={{
          marginTop: 18, padding: 12,
          background: "rgba(59,130,246,0.06)",
          border: "1px solid rgba(59,130,246,0.25)",
          fontSize: 13,
        }}
      >
        <FileSpreadsheet size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
        <strong>Tip:</strong> always try Preview first. The customer-status importers default to
        sync mode (overwriting where the spreadsheet differs). Use <em>Backfill only</em> if you want
        to keep manual edits in IMS.
      </div>

      {active && active.type === "modal" && (
        <CustomerReportImportModal
          kind={active.kind}
          label={active.label}
          description={active.desc}
          onClose={() => setActive(null)}
        />
      )}
    </div>
  );
}
